/**
 * The New invitation panel's paste box (R7, screen 17): one person per line,
 * as people actually paste them —
 *
 *   dana@acme.com
 *   Dana Rivera <dana@acme.com>
 *   "Rivera, Dana" <dana@acme.com>
 *   dana@acme.com, Dana Rivera
 *   Dana Rivera, dana@acme.com
 *   dana@acme.com; bo@acme.com          (separators on one line are fine too)
 *
 * — turned into the rows the CSV upload already accepts, so the paste box
 * and the CSV go through one server route and one deduplication. Addresses
 * are lowercased; a line with no address counts as unusable rather than
 * silently vanishing, so the count under the box says so.
 */

export interface PastedRecipient { email: string; name: string | null }

export interface PastedRecipients {
  recipients: PastedRecipient[];
  /** lines with no usable address */
  invalid: number;
  /** addresses repeated in the paste, dropped after the first */
  duplicates: number;
}

const EMAIL = /[^\s<>,;"']+@[^\s<>,;"']+\.[^\s<>,;"']+/;

/** The name half of a line, once the address and its brackets are out of it. */
function nameOf(chunk: string, email: string): string | null {
  const rest = chunk.replace(email, '').replace(/[<>]/g, '').replace(/^[\s,;:"'-]+|[\s,;:"'-]+$/g, '').trim();
  return rest ? rest.replace(/^"|"$/g, '').trim() || null : null;
}

export function parsePastedRecipients(text: string): PastedRecipients {
  const out: PastedRecipients = { recipients: [], invalid: 0, duplicates: 0 };
  const seen = new Set<string>();
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    // Several addresses on one line ("a@x.co; b@x.co") are several people; a
    // line with one address keeps whatever else is on it as the name.
    const chunks = (line.match(new RegExp(EMAIL.source, 'g')) || []).length > 1
      ? line.split(/[;,]/).map((c) => c.trim()).filter(Boolean)
      : [line];
    for (const chunk of chunks) {
      const m = chunk.match(EMAIL);
      if (!m) { out.invalid++; continue; }
      const email = m[0].toLowerCase();
      if (seen.has(email)) { out.duplicates++; continue; }
      seen.add(email);
      out.recipients.push({ email, name: nameOf(chunk, m[0]) });
    }
  }
  return out;
}

/** The rows as a CSV file for the upload route: a header, then name,email — quoted where a name needs it. */
export function recipientsToCsv(rows: PastedRecipient[]): string {
  const q = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  return ['name,email', ...rows.map((r) => `${q(r.name || '')},${q(r.email)}`)].join('\n') + '\n';
}
